import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UsageManagementService } from "@/lib/services/usage-management";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  CheckCircle,
  AlertTriangle,
  Clock,
  Gift,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { API_BASE_URL } from "@/config";

interface InviteDetails {
  invite_code: string;
  friend_email: string;
  friend_name: string | null;
  daily_limit: number;
  trial_days: number;
  expires_at: string;
  inviter_name?: string;
}

// Helper function to subscribe a user to the 'Profesional' plan
async function subscribeUserToProfessionalPlan(userId: string) {
  const { error } = await supabase.from("subscriptions").upsert({
    user_id: userId,
    plan_name: "Profesional",
    status: "active",
    created_at: new Date().toISOString(),
  });

  return { error };
}

export default function FriendInvite() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { user, signUp } = useAuth();
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [signupForm, setSignupForm] = useState({
    email: "",
    password: "",
    full_name: "",
  });
  const [error, setError] = useState<string | null>(null);

  const validateInvite = useCallback(async () => {
    if (!inviteCode) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call your Python backend instead of Supabase directly
      const response = await fetch(
        `${API_BASE_URL}/api/friend-invite/validate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            inviteCode: inviteCode,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.valid) {
        setInviteDetails({
          invite_code: data.invite.invite_code,
          friend_email: data.invite.friend_email,
          friend_name: data.invite.friend_name,
          daily_limit: data.invite.daily_limit,
          trial_days: data.invite.trial_days,
          expires_at: data.invite.expires_at,
        });

        // Pre-fill email if provided in invite
        if (data.invite.friend_email) {
          setSignupForm((prev) => ({
            ...prev,
            email: data.invite.friend_email,
          }));
        }
      } else {
        setError(data.error || "Invitación no válida o expirada");
      }
    } catch (err) {
      console.error("Error validating invite:", err);
      setError("Error al validar la invitación");
    } finally {
      setLoading(false);
    }
  }, [inviteCode]);

  useEffect(() => {
    if (inviteCode) {
      validateInvite();
    } else {
      setLoading(false);
      setError("No se proporcionó un código de invitación válido");
    }
  }, [inviteCode, validateInvite]);

  const acceptInvite = async () => {
    if (!inviteCode || !user) return;

    setAccepting(true);
    try {
      // Call backend to accept the invite
      const response = await fetch(`${API_BASE_URL}/api/friend-invite/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            (
              await supabase.auth.getSession()
            ).data.session?.access_token
          }`,
        },
        body: JSON.stringify({
          inviteCode: inviteCode,
          userId: user.id,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update the invite status locally
        await UsageManagementService.acceptFriendInvite(inviteCode, user.id);

        toast.success("¡Invitación aceptada exitosamente!");
        navigate("/dashboard");
      } else {
        toast.error(data.error || "Error al aceptar la invitación");
      }
    } catch (error) {
      console.error("Error accepting invite:", error);

      // Handle specific database constraint errors
      if (error instanceof Error) {
        if (
          error.message.includes("unique_user_id") ||
          error.message.includes("23505")
        ) {
          toast.error(
            "Ya has aceptado una invitación anteriormente. No puedes aceptar múltiples invitaciones."
          );
        } else {
          toast.error("Error al aceptar la invitación");
        }
      } else {
        // Check if it's an API response error with database constraint details
        const errorObj = error as any;
        if (
          errorObj?.code === "23505" ||
          errorObj?.message?.includes("unique_user_id")
        ) {
          toast.error(
            "Ya has aceptado una invitación anteriormente. No puedes aceptar múltiples invitaciones."
          );
        } else {
          toast.error("Error al aceptar la invitación");
        }
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleSignup = async () => {
    if (!inviteCode) return;

    try {
      const user = await signUp(signupForm.email, signupForm.password, {
        full_name: signupForm.full_name,
      });

      if (user) {
        // Accept the invite after successful signup
        await acceptInvite();
      } else {
        toast.error("Error al crear la cuenta");
      }
    } catch (error) {
      console.error("Signup error:", error);

      // Handle specific error messages
      if (error instanceof Error) {
        if (error.message.includes("Ya tienes una invitación activa")) {
          toast.error(error.message);
        } else if (error.message.includes("Password should contain at least one character of each")) {
          toast.error("La contraseña debe contener al menos un carácter de cada tipo: minúscula, mayúscula, número y carácter especial.");
        } else if (error.message.includes("User already registered")) {
          toast.error("Este correo electrónico ya está registrado.");
        } else {
          toast.error("Error durante el registro: " + error.message);
        }
      } else {
        toast.error("Error durante el registro");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Validando invitación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate("/")} className="w-full mt-4">
              Ir al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteDetails) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Gift className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">¡Invitación Especial!</CardTitle>
          <p className="text-muted-foreground">
            {inviteDetails?.inviter_name || "Un amigo"} te ha invitado a probar
            MiAsistenteLegalIA
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invite Details */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Límite diario:</span>
              <Badge variant="secondary">
                {inviteDetails?.daily_limit} requests
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Duración de prueba:</span>
              <Badge variant="secondary">
                {inviteDetails?.trial_days} días
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Expira:</span>
              <span className="text-sm text-muted-foreground">
                {inviteDetails?.expires_at
                  ? new Date(inviteDetails.expires_at).toLocaleDateString()
                  : "Próximamente"}
              </span>
            </div>
          </div>

          {/* Features Preview */}
          <div className="space-y-3">
            <h3 className="font-semibold">Lo que podrás hacer:</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Chat Legal IA
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Revisión de Contratos
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Generación de Documentos
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Investigación Legal
              </div>
            </div>
          </div>

          {user ? (
            // User is logged in - just accept invite
            <div className="space-y-4">
              <Alert>
                <Users className="h-4 w-4" />
                <AlertDescription>
                  Ya tienes una cuenta. Haz clic en "Aceptar Invitación" para
                  activar tu prueba.
                </AlertDescription>
              </Alert>
              <Button
                onClick={acceptInvite}
                disabled={accepting}
                className="w-full">
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Aceptando...
                  </>
                ) : (
                  <>
                    Aceptar Invitación
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            // User needs to sign up
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={signupForm.email}
                    onChange={(e) =>
                      setSignupForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="full_name">Nombre completo</Label>
                  <Input
                    id="full_name"
                    value={signupForm.full_name}
                    onChange={(e) =>
                      setSignupForm((prev) => ({
                        ...prev,
                        full_name: e.target.value,
                      }))
                    }
                    placeholder="Tu nombre completo"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={signupForm.password}
                    onChange={(e) =>
                      setSignupForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Crea una contraseña"
                  />
                </div>
              </div>
              <Button
                onClick={handleSignup}
                disabled={
                  accepting ||
                  !signupForm.email ||
                  !signupForm.password ||
                  !signupForm.full_name
                }
                className="w-full">
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando cuenta...
                  </>
                ) : (
                  <>
                    Crear Cuenta y Aceptar
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <p>
              Al aceptar esta invitación, tendrás acceso limitado durante{" "}
              {inviteDetails?.trial_days} días con un máximo de{" "}
              {inviteDetails?.daily_limit} requests por día.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
